import React from 'react';
import { useFela } from '../..';
import { CheckboxProps } from './CheckboxProps';
import { ThemeProps, WithTheme } from '../../Theme';

interface CheckboxColors {
  background: keyof ThemeProps['palette'];
  backgroundDisabled: keyof ThemeProps['palette'];
  border: keyof ThemeProps['palette'];
  borderDisabled: keyof ThemeProps['palette'];
  borderHover: keyof ThemeProps['palette'];
  borderHoverDisabled: keyof ThemeProps['palette'];
  borderInvalid: keyof ThemeProps['palette'];
  icon: keyof ThemeProps['palette'];
  iconDisabled: keyof ThemeProps['palette'];
}

const colors: CheckboxColors = {
  background: 'color5',
  backgroundDisabled: 'color9',
  border: 'color3',
  borderDisabled: 'color10',
  borderHover: 'color2',
  borderHoverDisabled: 'color10',
  borderInvalid: 'color8',
  icon: 'color2',
  iconDisabled: 'color10',
};

const backgroundImage = (color: string): string => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' data-dmid='CheckIcon' viewBox='0 0 24 24' width='48' height='48' role='img' pointer-events='none' data-reactroot=''><path fill='${color}' d='M19.67,8.2c0.69-0.69,1.03-1.5,0.75-1.78s-1.09,0-1.78,0.75l-7.83,8.01c-0.69,0.68-1.81,0.68-2.5,0l-2.85-2.89c-0.69-0.7-1.49-1.06-1.78-0.8c-0.29,0.26,0,1.04,0.7,1.78l3.85,3.9c0.69,0.69,1.82,0.69,2.51,0L19.67,8.2z'></path><path fill='none' stroke='${color}' stroke-miterlimit='10' stroke-width='3px' d='M19.67,8.2c0.69-0.69,1.03-1.5,0.75-1.78s-1.09,0-1.78,0.75l-7.83,8.01c-0.69,0.68-1.81,0.68-2.5,0l-2.85-2.89c-0.69-0.7-1.49-1.06-1.78-0.8c-0.29,0.26,0,1.04,0.7,1.78l3.85,3.9c0.69,0.69,1.82,0.69,2.51,0L19.67,8.2z'></path></svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
};

const defaultStyle = ({
  invalid,
  theme,
}: WithTheme<Pick<CheckboxProps, 'invalid'>>) => {
  const {
    background,
    backgroundDisabled,
    border,
    borderDisabled,
    borderHover,
    borderHoverDisabled,
    borderInvalid,
    icon,
    iconDisabled,
  } = colors;

  return {
    '[type=checkbox]': {
      outline: 'none',
      appearance: 'none',
      flex: '0 0 30px',
      minWidth: 30,
      minHeight: 30,
      maxWidth: 30,
      maxHeight: 30,
      // FIXME responsive hack
      opacity: 1,
      cursor: 'pointer',
      borderRadius: theme.radius.s.rem,
      border: `2px solid ${
        invalid ? theme.palette[borderInvalid].hex : theme.palette[border].hex
      }`,
      backgroundColor: theme.palette[background].hex,
      ':hover': {
        border: `solid 2px ${theme.palette[borderHover].hex}`,
      },
      ':active': {
        outlineStyle: 'none',
      },
      ':focus': {
        outline: 'none',
        borderColor: theme.palette.color2.hex,
        boxShadow: `0 0 5px ${theme.palette.color2.hex}`,
      },
      ':checked': {
        backgroundColor: theme.palette[background].hex,
        backgroundPosition: '0 -1px',
        backgroundSize: '26px 26px',
        backgroundRepeat: 'no-repeat',
        backgroundImage: backgroundImage(theme.palette[icon].hex),
      },
      ':disabled': {
        cursor: 'not-allowed',
        backgroundColor: theme.palette[backgroundDisabled].hex,
        borderColor: theme.palette[borderDisabled].hex,
        ':checked': {
          backgroundImage: backgroundImage(theme.palette[iconDisabled].hex),
        },
        ':hover': {
          border: `2px solid ${theme.palette[borderHoverDisabled].hex}`,
        },
        ':active': {
          outlineStyle: 'none',
        },
      },
    },
  };
};

const Checkbox = ({
  disabled,
  dmId = 'dm-input-checkbox',
  name = '',
  id,
  invalid,
  onChange,
  style = {},
  value,
  checked,
  ...otherProps
}: CheckboxProps): JSX.Element => {
  const { css } = useFela({ invalid });
  const className = css([style, defaultStyle]);

  return (
    <input
      data-designsystem
      checked={checked}
      className={className}
      data-dmid={dmId}
      disabled={disabled}
      id={id || dmId} // todo: dmId should be removed, as soon as id prop is required (myt-7757)
      name={name}
      onChange={onChange}
      type="checkbox"
      value={value}
      {...otherProps}
    />
  );
};

export default Checkbox;
